import gql from 'graphql-tag';
import { useQuery, provideApolloClient } from '@vue/apollo-composable';
import { ref, watch } from 'vue';
import apolloClient from './useApolloClient';

const GET_COMPANY_FILES = gql`
  query CompanyFilesQuery($companyId: ID!) {
    companyFiles(companyId: $companyId) {
      name
      date
      type
      folderName
    }
  }
`;

const GET_CONTACT_FILES = gql`
  query ContactFilesQuery($contactId: ID!) {
    contactFiles(contactId: $contactId) {
      name
      date
      type
      folderName
    }
  }
`;

// Global state management for files
const filesCache = ref(new Map());
const loadingStates = ref(new Map());
const errorStates = ref(new Map());

export default function useContactFilesQuery() {
  provideApolloClient(apolloClient);

  const fetchFiles = (entityId, entityType) => {
    if (!entityId || !entityType) return;

    const cacheKey = `${entityType}-${entityId}`;

    // Initialize states if not present
    if (!filesCache.value.has(cacheKey)) {
      filesCache.value.set(cacheKey, []);
    }
    if (!loadingStates.value.has(cacheKey)) {
      loadingStates.value.set(cacheKey, false);
    }
    if (!errorStates.value.has(cacheKey)) {
      errorStates.value.set(cacheKey, false);
    }

    // Set loading state
    loadingStates.value.set(cacheKey, true);
    errorStates.value.set(cacheKey, false);

    // Choose the correct query and variables based on entity type
    const query = entityType === 'company' ? GET_COMPANY_FILES : GET_CONTACT_FILES;
    const variables = entityType === 'company' ? { companyId: entityId } : { contactId: entityId };
    const resultKey = entityType === 'company' ? 'companyFiles' : 'contactFiles';

    const { result, error, loading, refetch } = useQuery(query, variables, { fetchPolicy: 'no-cache' });

    // Watch for results
    watch(result, (data) => {
      if (data?.[resultKey]) {
        filesCache.value.set(cacheKey, data[resultKey]);
      }
      loadingStates.value.set(cacheKey, false);
    });

    // Watch for errors
    watch(error, (err) => {
      if (err) {
        console.error(`Error fetching ${entityType} files:`, err);
        errorStates.value.set(cacheKey, true);
        loadingStates.value.set(cacheKey, false);
      }
    });

    return { result, error, loading, refetch };
  };

  const getFiles = (entityId, entityType) => {
    const cacheKey = `${entityType}-${entityId}`;
    return {
      data: filesCache.value.get(cacheKey) || [],
      loading: loadingStates.value.get(cacheKey) || false,
      error: errorStates.value.get(cacheKey) || false,
    };
  };

  const refetchFiles = (entityId, entityType) => {
    if (entityId && entityType) {
      fetchFiles(entityId, entityType);
    }
  };

  return {
    fetchFiles,
    getFiles,
    refetchFiles,
    filesCache,
    loadingStates,
    errorStates,
  };
}
